import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Hotel, Building, MapPin, User, FileText, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { fetchPropertyById, updateProperty } from "@/api/property"; // Import the function

// Define the property schema with validation
const propertySchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  location: z.string().min(5, { message: "Please enter a valid location." }),
  abn: z
    .string()
    .regex(/^\d{11}$/, { message: "ABN must be 11 digits." }),
  managerName: z.string().min(2, { message: "Manager name is required." }),
  rooms: z.number().min(1, { message: "Rooms must be at least 1." }),
});

// Type for our form values
type PropertyFormValues = z.infer<typeof propertySchema>;

const EditProperty = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State to store the current property fetched from the database
  const [property, setProperty] = useState<{
    propertyId: string;
    companyId: string;
    description: string;
    name: string;
    enabled: boolean;
    myob: boolean;
    rms: boolean;
    abn: string;
    rooms: string | number;
    dataDay: number;
  } | null>(null);

  // Initialize form with default values
  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      name: "",
      location: "",
      abn: "",
      managerName: "",
      rooms: 0, 
    },
  });

  useEffect(() => {
    if (!id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Property ID is missing.",
      });
      navigate("/properties");
      return;
    }

    const getProperty = async () => {
      try {
        setIsLoading(true);
        const response = await fetchPropertyById(id);
        if (response && response.body) {
          const fetchedProperty = {
            propertyId: id,
            companyId: sessionStorage.getItem("company_id") || "",
            description: response.body.description,
            name: response.body.name,
            enabled: response.body.enabled || false,
            myob: response.body.myob || false,
            rms: response.body.rms || false,
            abn: response.body.abn,
            rooms: response.body.rooms || "",
            dataDay: response.body.dataDay,
          };
          setProperty(fetchedProperty);

          form.reset({
            name: fetchedProperty.name,
            location: fetchedProperty.description,
            abn: fetchedProperty.abn,
            managerName: response.body.managerName || "",
            rooms: fetchedProperty.rooms, // Reset rooms field
          });
        } else {
          throw new Error("Property not found");
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load property details. Please try again.",
        });
        navigate("/properties");
      } finally {
        setIsLoading(false);
      }
    };

    getProperty();
  }, [id, form, toast, navigate]);

  // Handle form submission
  const onSubmit = async (data: PropertyFormValues) => {
    if (!id) return;

    setIsSubmitting(true);

    try {
      const payload = {
        propertyId: id,
        companyId: sessionStorage.getItem("company_id"),
        description: data.location,
        name: data.name,
        enabled: property?.enabled || false,
        myob: property?.myob || false,
        rms: property?.rms || false,
        abn: data.abn,
        rooms: data.rooms || "",
        dataDay: property?.dataDay || 4,
      };
      console.log("Payload", payload);
      const result = await updateProperty(payload);

      if (result.status === 200) {
        toast({
          title: "Property Updated",
          description: `${data.name} has been successfully updated.`,
        });

        navigate("/properties");
      } else {
        throw new Error("Failed to update property");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update property. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[70vh]">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-gray-500">Loading property data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Hotel className="mr-2 h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Edit Property</h1>
      </div>

      <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-sm border">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    Property Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Grand Hotel" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the official name of the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4" />
                    Location
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St, Sydney, NSW" {...field} />
                  </FormControl>
                  <FormDescription>
                    Full address of the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="abn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <FileText className="mr-2 h-4 w-4" />
                    ABN
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="12345678901" {...field} />
                  </FormControl>
                  <FormDescription>
                    Australian Business Number (11 digits).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="managerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    Manager Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="John Smith" {...field} />
                  </FormControl>
                  <FormDescription>
                    Name of the property manager.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rooms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    Number of Rooms
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="50"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the total number of rooms in the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/properties")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </>
                ) : (
                  "Update Property"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EditProperty;
